import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import axios from 'axios';
import SerializedItemLifecycleDetail from './SerializedItemLifecycleDetail';
import { formatPartMasterOptionLabel } from '../../utils/partMasterLabel';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const SerializedItemLifecycle = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);

  const [entityType, setEntityType] = useState('');
  const [partId, setPartId] = useState('');
  const [assemblyTypeId, setAssemblyTypeId] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [status, setStatus] = useState('');

  const [parts, setParts] = useState([]);
  const [assemblyTypes, setAssemblyTypes] = useState([]);
  const [dropdownsLoading, setDropdownsLoading] = useState(true);

  const [lifecycleDialogOpen, setLifecycleDialogOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);

  const fetchDropdowns = useCallback(async () => {
    setDropdownsLoading(true);
    try {
      const [partsRes, assemblyTypesRes] = await Promise.all([
        axios.get('/api/parts', { params: { isActive: 'true', pageSize: 1000, page: 1 } }),
        axios.get('/api/master-data/assembly-types', { params: { isActive: 'true' } })
      ]);
      const partsList = partsRes.data?.parts || (Array.isArray(partsRes.data) ? partsRes.data : partsRes.data?.items || []);
      setParts(partsList);
      setAssemblyTypes(Array.isArray(assemblyTypesRes.data) ? assemblyTypesRes.data : assemblyTypesRes.data?.data || []);
    } catch (err) {
      console.error('Error fetching dropdowns:', err);
    } finally {
      setDropdownsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        pageSize
      };
      if (entityType) params.entityType = entityType;
      if (partId) params.partId = partId;
      if (assemblyTypeId) params.assemblyTypeId = assemblyTypeId;
      if (serialNumber && serialNumber.trim()) params.serialNumber = serialNumber.trim();
      if (status) params.status = status;

      const res = await axios.get('/api/serialized-items', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total ?? 0);
    } catch (err) {
      console.error('Error fetching serialized items:', err);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, entityType, partId, assemblyTypeId, serialNumber, status]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleViewLifecycle = (id) => {
    setSelectedItemId(id);
    setLifecycleDialogOpen(true);
  };

  const handleCloseLifecycle = () => {
    setLifecycleDialogOpen(false);
    setSelectedItemId(null);
  };

  const getTypeLabel = (item) => {
    if (item.entityType === 'ASSEMBLY') {
      return item.assemblyTypeId?.name ?? item.assemblyTypeId ?? '-';
    }
    const part = item.partId;
    if (!part) return '-';
    const label = formatPartMasterOptionLabel(part);
    return label || '-';
  };

  const getCurrentLocation = (item) => {
    if (item.entityType === 'ASSEMBLY') {
      return item.currentAircraftRegistration || '-';
    }
    if (item.currentAircraftRegistration) {
      return item.currentAssemblyName
        ? `${item.currentAircraftRegistration} / ${item.currentAssemblyName}`
        : item.currentAircraftRegistration;
    }
    return '-';
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Serialized Item Lifecycle
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        View and search all serialized items (assemblies and components) and their full lifecycle history, independent of aircraft.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Filters</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Entity Type</InputLabel>
            <Select
              value={entityType}
              label="Entity Type"
              onChange={(e) => setEntityType(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="COMPONENT">Component</MenuItem>
              <MenuItem value="ASSEMBLY">Assembly</MenuItem>
            </Select>
          </FormControl>
          {entityType === 'COMPONENT' && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Part</InputLabel>
              <Select
                value={partId}
                label="Part"
                onChange={(e) => setPartId(e.target.value)}
                disabled={dropdownsLoading}
              >
                <MenuItem value="">All parts</MenuItem>
                {parts.map((p) => (
                  <MenuItem key={p._id} value={p._id}>
                    {formatPartMasterOptionLabel(p) || p._id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {entityType === 'ASSEMBLY' && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Assembly Type</InputLabel>
              <Select
                value={assemblyTypeId}
                label="Assembly Type"
                onChange={(e) => setAssemblyTypeId(e.target.value)}
                disabled={dropdownsLoading}
              >
                <MenuItem value="">All types</MenuItem>
                {assemblyTypes.map((at) => (
                  <MenuItem key={at._id} value={at._id}>{at.name || at._id}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            size="small"
            label="Serial number"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            placeholder="Search by serial"
            sx={{ minWidth: 160 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="RETIRED">Retired</MenuItem>
              <MenuItem value="SCRAPPED">Scrapped</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={() => fetchItems()}>
            Apply
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Display ID</TableCell>
              <TableCell>Global ID</TableCell>
              <TableCell>Entity Type</TableCell>
              <TableCell>Part / Assembly Type</TableCell>
              <TableCell>Serial Number</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Current Location</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  No serialized items found.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item._id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{item.displayId || '-'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{item.globalId || '-'}</TableCell>
                  <TableCell>
                    <Chip label={item.entityType} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{getTypeLabel(item)}</TableCell>
                  <TableCell>{item.serialNumber || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.status || 'ACTIVE'}
                      size="small"
                      color={item.status === 'ACTIVE' ? 'success' : item.status === 'SCRAPPED' ? 'error' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{getCurrentLocation(item)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleViewLifecycle(item._id)}
                      title="View lifecycle"
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        />
      </TableContainer>

      <Dialog open={lifecycleDialogOpen} onClose={handleCloseLifecycle} maxWidth="md" fullWidth>
        <DialogTitle>Serialized Item Lifecycle History</DialogTitle>
        <DialogContent>
          {selectedItemId && (
            <SerializedItemLifecycleDetail serializedItemId={selectedItemId} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLifecycle}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SerializedItemLifecycle;
